"use client";

import { useState } from "react";

import { COMPANY_PRIVACY_POLICY_URL } from "../../../constants";
import StripeCardSetDialog from "../stripe-card-set-dialog";
import type { StripeCardSetDialogMessages } from "../stripe-card-set-dialog";
import { type AnalyticsAttrs, pickAnalyticsAttrs } from "../ui/analytics-attrs";
import { Link } from "../ui/link";

export type CreditcardConsentGateMessages = {
  consentText: string;
  privacyPolicyLinkText: string;
  agreeCheckboxLabel: string;
  dialogMessages?: StripeCardSetDialogMessages;
};

const JA_MESSAGES: CreditcardConsentGateMessages = {
  consentText:
    "お客様の情報は、決済処理のため決済代行会社・カード会社（国外含む）に提供されます。詳細は{privacyPolicy}をご確認ください。",
  privacyPolicyLinkText: "プライバシーポリシー",
  agreeCheckboxLabel: "上記に同意します",
};

/**
 * 第三者提供・国外移転についての同意取得（チェックボックス）と、
 * 同意済みの場合のみ有効化される「クレジットカード登録」ボタン、Stripeダイアログの起動を1つに集約する。
 * 同意済みかどうか・ダイアログの開閉状態は呼び出し元に漏らさない。
 */
export function CreditcardConsentGate({
  buttonLabel,
  organizationId,
  messages = JA_MESSAGES,
  onButtonClick,
  ...analyticsAttrs
}: AnalyticsAttrs & {
  buttonLabel: string;
  // 指定した場合にはその組織のカードを、指定しない場合は個人のカードを更新。
  organizationId?: string;
  messages?: CreditcardConsentGateMessages;
  // 呼び出し元固有のトラッキング等、ダイアログを開く前に行いたい副作用のフック。
  onButtonClick?: () => void;
}) {
  const [agreed, setAgreed] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  const [consentTextBeforeLink, consentTextAfterLink] =
    messages.consentText.split("{privacyPolicy}");

  return (
    <div className="flex w-full flex-col items-center gap-[16px]">
      <p className="text-skogecha w-full text-[12px] leading-[1.8] wrap-break-word">
        {consentTextBeforeLink}
        <Link
          href={COMPANY_PRIVACY_POLICY_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="underline"
          data-analytics-id="payment_method_clicked_privacy_policy"
        >
          {messages.privacyPolicyLinkText}
        </Link>
        {consentTextAfterLink}
      </p>
      <label className="flex w-full items-start gap-x-[8px] text-[12px]">
        <input
          type="checkbox"
          checked={agreed}
          onChange={(e) => setAgreed(e.target.checked)}
          className="mt-[3px] h-[18px] w-[18px] shrink-0 cursor-pointer"
          data-testid="consent-checkbox"
        />
        <span className="text-skogecha">{messages.agreeCheckboxLabel}</span>
      </label>
      <button
        type="button"
        className="btn-primary w-full max-w-[280px] px-[16px] py-[12px] whitespace-normal"
        disabled={!agreed}
        onClick={() => {
          setDialogOpen(true);
          onButtonClick?.();
        }}
        data-testid="register-card-button"
        {...pickAnalyticsAttrs(analyticsAttrs)}
      >
        {buttonLabel}
      </button>
      <StripeCardSetDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        organizationId={organizationId}
        messages={messages.dialogMessages}
      />
    </div>
  );
}
